import React, { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { PawPrint, Plus, X, Trash2, Edit3, Camera, Sparkles, Copy, Check, FileText } from "lucide-react";

const SPECIES = [
 ["dog", "Dog"], ["cat", "Cat"], ["bird", "Bird"], ["fish", "Fish"],
 ["reptile", "Reptile"], ["small_mammal", "Small mammal"], ["other", "Other"],
];

const blank = {
 name: "", species: "dog", breed: "", age: "", weight: "",
 microchip_number: "", council_registration: "",
 desexed: false, vaccinated: false, references: "", notes: ""
};

function PhotoStrip({ pet, onChange }) {
 const fileRef = useRef();
 const ids = pet.photo_document_ids || [];
 const token = localStorage.getItem("tt_token");

 const upload = async (file) => {
 const fd = new FormData();
 fd.append("file", file);
 try {
 await api.post(`/pets/${pet.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
 onChange();
 } catch (e) { alert(e.response?.data?.detail || "Photo upload failed"); }
 if (fileRef.current) fileRef.current.value = "";
 };

 const remove = async (docId) => {
 if (!window.confirm("Remove this photo?")) return;
 await api.delete(`/pets/${pet.id}/photos/${docId}`);
 onChange();
 };

 return (
 <div className="flex gap-2 flex-wrap items-center mt-3" data-testid={`pet-photos-${pet.id}`}>
 {ids.map((docId) => (
 <div key={docId} className="relative group w-16 h-16 bg-[#FAF9F6] border border-[#E5E4DE] overflow-hidden">
 <img
 src={`${API}/documents/${docId}/download?token=${token}&inline=1`}
 alt="pet"
 className="w-full h-full object-cover"
 onError={(e) => { e.target.style.display = "none"; }}
 />
 <button
 onClick={() => remove(docId)}
 className="absolute top-0 right-0 bg-[#0D2B52] text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
 data-testid={`remove-photo-${docId}`}
 ><X size={11} strokeWidth={2} /></button>
 </div>
 ))}
 <button
 onClick={() => fileRef.current?.click()}
 className="w-16 h-16 border-2 border-dashed border-[#E5E4DE] hover:border-[#1B6C62] hover:bg-[#E6F5F2]/40 flex items-center justify-center text-[#6B7280] hover:text-[#1B6C62] transition-colors"
 title="Add photo"
 data-testid={`add-photo-${pet.id}`}
 ><Camera size={18} strokeWidth={1.5} /></button>
 <input ref={fileRef} type="file" accept="image/*" className="hidden"
 onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
 </div>
 );
}

function DraftAppModal({ pet, onClose }) {
 const [mode, setMode] = useState("covering_letter");
 const [formFile, setFormFile] = useState(null);
 const [busy, setBusy] = useState(false);
 const [result, setResult] = useState("");
 const [copied, setCopied] = useState(false);

 const generate = async () => {
 setBusy(true); setResult("");
 try {
 const fd = new FormData();
 fd.append("mode", mode);
 if (formFile) fd.append("form_file", formFile);
 const { data } = await api.post(`/pets/${pet.id}/draft-application`, fd, {
 headers: { "Content-Type": "multipart/form-data" }
 });
 setResult(data.text);
 } catch (e) {
 alert(e.response?.data?.detail || "Draft failed");
 } finally { setBusy(false); }
 };

 const copy = () => {
 navigator.clipboard.writeText(result);
 setCopied(true);
 setTimeout(() => setCopied(false), 1500);
 };

 return (
 <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
 <div className="bg-[#FAF9F6] border border-[#E5E4DE] rounded-sm max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()} data-testid="draft-app-modal">
 <div className="flex justify-between items-center p-6 border-b border-[#E5E4DE]">
 <div className="flex items-center gap-2">
 <Sparkles size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
 <h3 className="font-serif text-2xl">Draft application for {pet.name}</h3>
 </div>
 <button onClick={onClose} className="btn-ghost p-1.5"><X size={18} strokeWidth={1.5} /></button>
 </div>

 <div className="p-6 space-y-5">
 <div>
 <label className="label-mono block mb-2">What should the AI produce?</label>
 <div className="grid sm:grid-cols-2 gap-3">
 <button onClick={() => setMode("covering_letter")}
 className={`text-left p-4 border-2 transition-colors ${mode === "covering_letter" ? "border-[#0D2B52] bg-white" : "border-[#E5E4DE] hover:border-[#1B6C62]"}`}
 data-testid="mode-letter">
 <div className="font-serif text-lg text-[#0D2B52] mb-1">Covering letter</div>
 <div className="text-xs text-[#6B7280]">A polished letter you attach to the application. Highlights {pet.name}'s strengths.</div>
 </button>
 <button onClick={() => setMode("fill_form")}
 className={`text-left p-4 border-2 transition-colors ${mode === "fill_form" ? "border-[#0D2B52] bg-white" : "border-[#E5E4DE] hover:border-[#1B6C62]"}`}
 data-testid="mode-fill">
 <div className="font-serif text-lg text-[#0D2B52] mb-1">Fill the form</div>
 <div className="text-xs text-[#6B7280]">Upload the agent's PDF/image form and we'll map fields to your pet's data.</div>
 </button>
 </div>
 </div>

 <div>
 <label className="label-mono block mb-2">
 Agent's pet application form (optional, image or PDF)
 {mode === "fill_form" && <span className="text-[#B35D4C]"> *recommended</span>}
 </label>
 <div className="flex items-center gap-3">
 <label className="btn-secondary text-sm inline-flex items-center gap-2 cursor-pointer">
 <FileText size={14} strokeWidth={1.5} /> {formFile ? formFile.name.slice(0,30) : "Choose file"}
 <input type="file" accept="image/*,application/pdf" className="hidden"
 onChange={(e) => setFormFile(e.target.files[0] || null)}
 data-testid="form-file-input" />
 </label>
 {formFile && <button onClick={() => setFormFile(null)} className="btn-ghost p-1.5"><X size={14} strokeWidth={1.5} /></button>}
 </div>
 </div>

 <button onClick={generate} disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="generate-app">
 <Sparkles size={15} strokeWidth={1.5} />
 {busy ? "Drafting (our AI is reading)…" : `Generate ${mode === "fill_form" ? "form mapping" : "covering letter"}`}
 </button>

 {result && (
 <div className="card" data-testid="draft-result">
 <div className="flex justify-between items-center mb-3">
 <span className="label-mono">Your draft</span>
 <button onClick={copy} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="copy-draft">
 {copied ? (<><Check size={12} strokeWidth={2} /> Copied</>) : (<><Copy size={12} strokeWidth={1.5} /> Copy</>)}
 </button>
 </div>
 <div className="whitespace-pre-wrap font-serif text-[15px] leading-relaxed text-[#1A1A1A]">{result}</div>
 <p className="text-xs text-[#6B7280] mt-4 italic">Review before sending. Edit any field that says "(needs your input)". This is general drafting help, not legal advice.</p>
 </div>
 )}
 </div>
 </div>
 </div>
 );
}

export default function PetsTab({ caseId }) {
 const [pets, setPets] = useState([]);
 const [editing, setEditing] = useState(null);
 const [form, setForm] = useState(blank);
 const [drafting, setDrafting] = useState(null);

 const load = () => api.get(`/cases/${caseId}/pets`).then(({ data }) => setPets(data));
 // eslint-disable-next-line react-hooks/exhaustive-deps
 useEffect(() => { load(); }, [caseId]);

 const submit = async (e) => {
 e.preventDefault();
 if (editing === "new") await api.post(`/cases/${caseId}/pets`, form);
 else await api.put(`/pets/${editing}`, form);
 setEditing(null); setForm(blank); load();
 };

 const startEdit = (p) => {
 setEditing(p.id);
 setForm({
 name: p.name||"", species: p.species||"dog", breed: p.breed||"",
 age: p.age||"", weight: p.weight||"",
 microchip_number: p.microchip_number||"", council_registration: p.council_registration||"",
 desexed: !!p.desexed, vaccinated: !!p.vaccinated,
 references: p.references||"", notes: p.notes||""
 });
 };

 const remove = async (id) => {
 if (!window.confirm("Delete this pet?")) return;
 await api.delete(`/pets/${id}`); load();
 };

 return (
 <div className="space-y-6" data-testid="pets-tab">
 <div className="card bg-[#E6F5F2]/40">
 <div className="flex items-start gap-3">
 <PawPrint size={20} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5" />
 <div className="flex-1">
 <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Pet records &amp; applications</h3>
 <p className="text-sm text-[#333] leading-relaxed">
 Store your pet's details, photos, vaccinations and references. When you find a property,
 hit <em>Draft application</em>. upload the agent's pet form and the AI will either fill
 it field-by-field, or write a polished covering letter using your pet's strengths and the
 state's pet rules.
 </p>
 </div>
 </div>
 </div>

 <div className="flex justify-between items-center">
 <p className="text-sm text-[#6B7280]">{pets.length} {pets.length === 1 ? "pet" : "pets"} on file</p>
 {editing === null && (
 <button onClick={() => { setForm(blank); setEditing("new"); }} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="add-pet-btn">
 <Plus size={15} strokeWidth={1.5} /> Add a pet
 </button>
 )}
 </div>

 {editing !== null && (
 <form onSubmit={submit} className="card space-y-4" data-testid="pet-form">
 <div className="flex justify-between items-center">
 <h3 className="font-serif text-xl">{editing === "new" ? "New pet" : "Edit pet"}</h3>
 <button type="button" onClick={() => { setEditing(null); setForm(blank); }} className="btn-ghost p-1.5"><X size={18} strokeWidth={1.5} /></button>
 </div>
 <div className="grid sm:grid-cols-2 gap-4">
 <div><label className="label-mono block mb-1.5">Name</label>
 <input required className="input-field" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="pet-name" /></div>
 <div><label className="label-mono block mb-1.5">Species</label>
 <select className="input-field" value={form.species} onChange={e=>setForm({...form,species:e.target.value})} data-testid="pet-species">
 {SPECIES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
 </select></div>
 <div><label className="label-mono block mb-1.5">Breed</label>
 <input className="input-field" value={form.breed} onChange={e=>setForm({...form,breed:e.target.value})} /></div>
 <div><label className="label-mono block mb-1.5">Age</label>
 <input className="input-field" placeholder="e.g. 3 years" value={form.age} onChange={e=>setForm({...form,age:e.target.value})} /></div>
 <div><label className="label-mono block mb-1.5">Weight</label>
 <input className="input-field" placeholder="e.g. 22 kg" value={form.weight} onChange={e=>setForm({...form,weight:e.target.value})} /></div>
 <div><label className="label-mono block mb-1.5">Microchip #</label>
 <input className="input-field" value={form.microchip_number} onChange={e=>setForm({...form,microchip_number:e.target.value})} /></div>
 <div className="sm:col-span-2"><label className="label-mono block mb-1.5">Council registration</label>
 <input className="input-field" value={form.council_registration} onChange={e=>setForm({...form,council_registration:e.target.value})} /></div>
 <label className="flex items-center gap-2 text-sm">
 <input type="checkbox" checked={form.desexed} onChange={e=>setForm({...form,desexed:e.target.checked})} /> Desexed
 </label>
 <label className="flex items-center gap-2 text-sm">
 <input type="checkbox" checked={form.vaccinated} onChange={e=>setForm({...form,vaccinated:e.target.checked})} /> Up-to-date vaccinations
 </label>
 </div>
 <div><label className="label-mono block mb-1.5">References (previous landlords / vet etc.)</label>
 <textarea rows={2} className="input-field" value={form.references} onChange={e=>setForm({...form,references:e.target.value})} /></div>
 <div><label className="label-mono block mb-1.5">Notes (behaviour, training, etc.)</label>
 <textarea rows={2} className="input-field" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} /></div>
 <div className="flex justify-end gap-2">
 <button type="button" onClick={() => { setEditing(null); setForm(blank); }} className="btn-secondary text-sm">Cancel</button>
 <button type="submit" className="btn-primary text-sm" data-testid="submit-pet">{editing === "new" ? "Add pet" : "Save changes"}</button>
 </div>
 </form>
 )}

 {pets.length === 0 && editing === null ? (
 <div className="text-sm text-[#6B7280] italic text-center py-8">No pets on file yet.</div>
 ) : (
 <div className="grid sm:grid-cols-2 gap-4">
 {pets.map((p) => (
 <div key={p.id} className="card" data-testid={`pet-${p.id}`}>
 <div className="flex justify-between items-start mb-2">
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 bg-[#E6F5F2] border border-[#1B6C62]/30 flex items-center justify-center"><PawPrint size={18} strokeWidth={1.5} className="text-[#1B6C62]" /></div>
 <div>
 <h4 className="font-serif text-lg text-[#0D2B52]">{p.name}</h4>
 <p className="text-xs text-[#6B7280] capitalize">{SPECIES.find(s=>s[0]===p.species)?.[1] || p.species}{p.breed && ` · ${p.breed}`}</p>
 </div>
 </div>
 <div className="flex gap-1">
 <button onClick={() => startEdit(p)} className="btn-ghost p-1.5"><Edit3 size={14} strokeWidth={1.5} /></button>
 <button onClick={() => remove(p.id)} className="btn-ghost p-1.5 hover:text-[#B35D4C]"><Trash2 size={14} strokeWidth={1.5} /></button>
 </div>
 </div>
 <div className="grid grid-cols-2 gap-2 text-xs text-[#6B7280]">
 {p.age && <div>Age: <span className="text-[#1A1A1A]">{p.age}</span></div>}
 {p.weight && <div>Weight: <span className="text-[#1A1A1A]">{p.weight}</span></div>}
 {p.microchip_number && <div className="col-span-2">Chip: <span className="font-mono text-[#1A1A1A]">{p.microchip_number}</span></div>}
 </div>
 <div className="flex gap-2 mt-3 text-xs">
 {p.desexed && <span className="bg-[#E6F5F2] text-[#1B6C62] px-2 py-0.5 rounded-sm">Desexed</span>}
 {p.vaccinated && <span className="bg-[#E6F5F2] text-[#1B6C62] px-2 py-0.5 rounded-sm">Vaccinated</span>}
 </div>
 <PhotoStrip pet={p} onChange={load} />
 <button
 onClick={() => setDrafting(p)}
 className="btn-teal text-sm w-full mt-4 inline-flex items-center justify-center gap-2"
 data-testid={`draft-app-${p.id}`}
 ><Sparkles size={14} strokeWidth={1.5} /> Draft pet application</button>
 </div>
 ))}
 </div>
 )}

 {drafting && <DraftAppModal pet={drafting} onClose={() => setDrafting(null)} />}
 </div>
 );
}
